import React, { useEffect } from "react";

import { navigate } from "gatsby";

import { css, useTheme } from "@emotion/react";

import checkoutSteps from "../../../../providers/HelCheckoutSteps";
import {
    storageLocal as storage,
    storageSession as session,
} from "../../../../utils/StorageHandler";
import resolveWebView from "../../../../utils/webViewRedirect";
import Hel2021PriceActionDisclaimer from "../../../shared/Campaign/components/Hel2021PriceActionDisclaimer";
import Container from "../../../shared/Container";
import Margin from "../../../shared/Margin";
import OrderStepper from "../../../shared/OrderStepper";
import useSnackbar from "../../../shared/Snackbar/store";
import useCalculatorStore from "../CalculatorPage/store";
import BottomBar from "./BottomBar";
import StepButtons from "./StepButtons";
import useStepButtonsStore from "./StepButtons/store";
import useStepDeliveryAddressStore from "./StepDeliveryAddress/store";
import useStepDeliveryDetailsStore from "./StepDeliveryDetails/store";
import useAgbStore from "./StepOverview/Agb/store";
import useNewsletterStore from "./StepOverview/Newsletter/store";
import useStepOverviewStore from "./StepOverview/store";
import useVoucherStore from "./StepOverview/Voucher/store";
import useStepPaymentTypeStore from "./StepPaymentType/store";
import useStepProductChoiceStore from "./StepProductChoice/store";
import useCheckoutStore from "./store";

const CheckoutPage = () => {
    const { notify } = useSnackbar();
    const theme = useTheme();
    const [
        checkoutData,
        prices,
        initCheckout,
        setCheckoutData,
        detailsChanged,
    ] = useCheckoutStore((state) => [
        state.checkoutData,
        state.getPrices(),
        state.initCheckout,
        state.setCheckoutData,
        state.detailsChanged,
    ]);
    const [
        deliveryTime,
        deliveryTimeType,
        deliveryTimeFrame,
        deliveryTimeWishDate,
        antiFreeze,
        hose,
        climateNeutral,
        promotionCode,
        setDeliveryTime,
        storeDeliveryDetailsToServer,
        storeClimateNeutralToStorage,
        storeOrderCommentToStorage,
        storePromotionCodeToStorage,
        storeHoseToStorage,
        storeDeliveryTimeToStorage,
        storeDeliveryTimeTypeToStorage,
        storeAntiFreezeToStorage,
        storeDeliveryTimeFrameToStorage,
        storeDeliveryTimeWishDateToStorage,
        setClimateNeutral,
    ] = useStepDeliveryDetailsStore((state) => [
        state.deliveryTime,
        state.deliveryTimeType,
        state.deliveryTimeFrame,
        state.deliveryTimeWishDate,
        state.antiFreeze,
        state.hose,
        state.climateNeutral,
        state.promotionCode,
        state.setDeliveryTime,
        state.storeDeliveryDetailsToServer,
        state.storeClimateNeutralToStorage,
        state.storeOrderCommentToStorage,
        state.storePromotionCodeToStorage,
        state.storeHoseToStorage,
        state.storeDeliveryTimeToStorage,
        state.storeDeliveryTimeTypeToStorage,
        state.storeAntiFreezeToStorage,
        state.storeDeliveryTimeFrameToStorage,
        state.storeDeliveryTimeWishDateToStorage,
        state.setClimateNeutral,
    ]);

    const [zipcode, amount, up, calculate] = useCalculatorStore((state) => [
        state.zipcode,
        state.amount,
        state.up,
        state.calculate,
    ]);

    const [
        checkoutStep,
        stepButtonAction,
        steps,
        currentStepIndex,
        previousStepIndex,
        setStepButtonAction,
        setCurrentStepIndex,
    ] = useStepButtonsStore((state) => [
        state.checkoutStep,
        state.stepButtonAction,
        state.steps,
        state.currentStepIndex,
        state.previousStepIndex,
        state.setStepButtonAction,
        state.setCurrentStepIndex,
    ]);

    const [
        storeProductIsPremiumToStorage,
        storeProductToServer,
        setProductIsPremium,
    ] = useStepProductChoiceStore((state) => [
        state.storeProductIsPremiumToStorage,
        state.storeProductToServer,
        state.setProductIsPremium,
    ]);

    const [
        storeDeliveryAddressToStorage,
        storeInvoiceAddressToStorage,
        storeDeliveryAddressToServer,
    ] = useStepDeliveryAddressStore((state) => [
        state.storeDeliveryAddressToStorage,
        state.storeInvoiceAddressToStorage,
        state.storeDeliveryAddressToServer,
    ]);

    const [
        storePaymentTypeToStorage,
        storePaymentTypeToServer,
        setPaymentType,
    ] = useStepPaymentTypeStore((state) => [
        state.storePaymentTypeToStorage,
        state.storePaymentTypeToServer,
        state.setPaymentType,
    ]);

    const [agbChecked] = useAgbStore((state) => [state.agbChecked]);

    const [newsletterChecked] = useNewsletterStore((state) => [
        state.newsletterChecked,
    ]);

    const { voucher, resetVoucher } = useVoucherStore((state) => ({
        voucher: state.voucher,
        resetVoucher: state.reset,
    }));

    const [sendOrderToServer, setCheckoutIsExpired, checkoutIsExpired] =
        useStepOverviewStore((state) => [
            state.sendOrderToServer,
            state.setCheckoutIsExpired,
            state.checkoutIsExpired,
        ]);

    useEffect(() => {
        let index = checkoutSteps.findIndex(
            (step) => step.url === checkoutStep
        );
        if (index === -1) {
            void navigate(resolveWebView("/heizoel/bestellung/lieferdetails/"));
        } else {
            setCurrentStepIndex(index);
        }
    }, [checkoutStep]);

    useEffect(() => {
        calculate(zipcode, amount, up, notify);
    }, []);

    /**
     * When the details changed (state) the price should be updated.
     */
    useEffect(() => {
        async function storeDelivery() {
            const checkoutDataResponse = await storeDeliveryDetailsToServer(
                checkoutData,
                notify,
                true
            );
            setCheckoutData(checkoutDataResponse);
        }

        if (detailsChanged > 0) {
            void storeDelivery();
        }
    }, [detailsChanged]);

    const currentStepComponent = checkoutSteps[currentStepIndex].component;
    const currentStep = checkoutSteps[currentStepIndex].step;

    const onStepIndexChange = async () => {
        window.scrollTo(0, 0);

        async function storeProduct() {
            storeProductIsPremiumToStorage();
            const checkoutDataResponse = await storeProductToServer(
                checkoutData,
                notify
            );
            setCheckoutData(checkoutDataResponse, true);
        }

        async function storeDeliveryDetails() {
            storeClimateNeutralToStorage();
            storeOrderCommentToStorage();
            storePromotionCodeToStorage();
            storeHoseToStorage();
            storeDeliveryTimeToStorage();
            storeDeliveryTimeTypeToStorage();
            storeAntiFreezeToStorage();
            storeDeliveryTimeFrameToStorage();
            storeDeliveryTimeWishDateToStorage();
            // Store to server
            const checkoutDataResponse = await storeDeliveryDetailsToServer(
                checkoutData,
                notify
            );
            setCheckoutData(checkoutDataResponse, true);
        }

        async function storeDeliveryAddress() {
            storeDeliveryAddressToStorage();
            storeInvoiceAddressToStorage();
            // Store to server
            const checkoutDataResponse = await storeDeliveryAddressToServer(
                checkoutData,
                notify
            );
            setCheckoutData(checkoutDataResponse, true);
        }

        async function storePaymentType() {
            storePaymentTypeToStorage();
            // Store to server
            const checkoutDataResponse = await storePaymentTypeToServer(
                checkoutData,
                notify
            );
            setCheckoutData(checkoutDataResponse, true);
        }

        async function storeCheckout() {
            const checkoutDataResponse = await sendOrderToServer(
                checkoutData,
                newsletterChecked,
                agbChecked,
                voucher,
                notify
            );

            resetVoucher();
            setCheckoutData(checkoutDataResponse, true);
        }

        if (
            session.get("hel:checkout") !== null &&
            checkoutSteps[previousStepIndex] &&
            stepButtonAction === "next"
        ) {
            const previousUrl = checkoutSteps[previousStepIndex].url;
            const currentStep = checkoutSteps[currentStepIndex].url;
            if (previousUrl === "produkt" && currentStep === "lieferdetails") {
                void storeProduct();
            }
            if (
                previousUrl === "lieferdetails" &&
                currentStep === "lieferadresse"
            ) {
                void storeDeliveryDetails();
            }
            if (previousUrl === "lieferadresse" && currentStep === "zahlart") {
                void storeDeliveryAddress();
            }
            if (previousUrl === "zahlart" && currentStep === "uebersicht") {
                void storePaymentType();
            }
            if (previousUrl === "uebersicht" && currentStep === "erfolg") {
                if (session.getJson("hel:order") === null) {
                    void storeCheckout();
                }
            }
        }
        setStepButtonAction("");
    };

    /**
     * Handle the current step index.
     */
    useEffect(() => {
        onStepIndexChange();
    }, [currentStepIndex]);

    const deliveryTimeIds = {
        1: "standard",
        2: "short",
        3: "express48",
        4: "express24",
    };

    useEffect(() => {
        const onCheckout = async () => {
            async function initCheckoutFunction() {
                const response = await initCheckout(
                    zipcode,
                    amount,
                    up,
                    notify
                );
                storage.set("hel:climate-neutral", response.climate_neutral);
                setClimateNeutral(response.climate_neutral);
                setProductIsPremium(response.is_premium);
                storeProductIsPremiumToStorage(response.is_premium);
                setPaymentType(response.payment_type_id);
                storePaymentTypeToStorage();

                // Bugfix: Convert numeric value to a string representative and set the
                // products preselected choice in the storage and the store.
                const deliveryTimeString =
                    deliveryTimeIds[response.delivery_time_id] ??
                    deliveryTimeIds[1];
                setDeliveryTime(deliveryTimeString);
                storeDeliveryTimeToStorage(deliveryTimeString);
            }

            setCheckoutIsExpired(false);
            /**
             * When a user calls a checkout page without session, he can place an order
             * without valid data, causing errors.
             * If the user has no session, while he needs one, redirect him back to the calculator.
             */
            if (
                storage.getBoolean("hel:needs-session") === true &&
                session.getJson("hel:checkout", null) === null
            ) {
                await navigate(resolveWebView("/heizoel/"));
                void notify(
                    "Sitzung abgelaufen",
                    "Die Sitzung dieser Bestellung ist abgelaufen. Starte bitte eine Neue.",
                    "default",
                    { timeout: 10000 }
                );
                return;
            }

            // Init the checkout, if not exists.
            if (session.get("hel:checkout") === null) {
                await void initCheckoutFunction();
            }
        };

        onCheckout();
    }, []);

    /*

    AE: Strange side effects :feelsWeirdMan

    useEffect(() => {
        async function fetchData() {
            const checkoutDataResponse = await storeDeliveryDetailsToServer(
                sessionStorage,
                notify,
                true
            );
            setCheckoutData(checkoutDataResponse);
        }
        if (session.get("hel:checkout") !== null) {
            if (checkoutData.user_hash) {
                void fetchData();
            }
        }
    }, [
        deliveryTime,
        deliveryTimeType,
        deliveryTimeFrame,
        deliveryTimeWishDate,
        antiFreeze,
        hose,
        climateNeutral,
        promotionCode,
        storeDeliveryDetailsToServer,
        notify,
        setCheckoutData,
    ]);*/

    const newStyles = css`
        font-family: var(--font-family-vito);
        color: ${theme.colors.blue.toString()};
    `;

    return (
        <React.Fragment>
            <Container slim css={newStyles}>
                {currentStep !== null && (
                    <OrderStepper steps={steps} currentStep={currentStep} />
                )}

                {currentStepComponent}
                {currentStepIndex < checkoutSteps.length - 1 && <StepButtons />}
                {currentStep === 3 &&
                    prices.priceTotalWithAction &&
                    !checkoutIsExpired && (
                        <Margin top="10">
                            <Hel2021PriceActionDisclaimer />
                        </Margin>
                    )}
            </Container>
            {prices && <BottomBar />}
        </React.Fragment>
    );
};

export default CheckoutPage;
